/* eslint camelcase: 0 */
import { faxios } from '@utils/faxios';
import config from '@/config/client';
import { getInstance as getAuth0Instance } from '@/auth';
import { applicationIdentifier } from './applicationIdentifier';
import { LeaseResponse, LeaseResponseStatus, KickoutResponseStatus, GetLeaseOptions } from '@common/license/types';

const getRequestHeaders = async (body: boolean = false) => {
  const token = await getAuth0Instance().getTokenSilently();

  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };

  if (body) {
    headers['content-type'] = 'application/json;charset=UTF-8';
  }

  return headers;
};

const getRequestBody = () => ({
  application_platform: 'cloud',
  application_version: '0.0', // Only for Desktop Apps
  agent_identifier: applicationIdentifier(),
});

const getTrialAvailability = async () => {
  const headers = await getRequestHeaders(true);
  const body = getRequestBody();

  // Ignore error. We just assume the user has no trial option
  const { data } = await faxios.post(`${config.acoHost}/api/cantrial`, body, {
    headers,
  });

  return data;
};

/**
 * @param options
 * @param options.name - The name of the options.
 * @param options.department - The employee's department.
 */
const getLeaseFromApi = async (options: GetLeaseOptions = {}): Promise<LeaseResponse> => {
  const headers = await getRequestHeaders(true);

  const body = {
    ...getRequestBody(),
    ...options,
  };

  let leaseResponse: LeaseResponse = { status: null, data: null };

  try {
    const response = await faxios.post(`${config.acoHost}/api/lease`, body, {
      headers,
      validateStatus(status) {
        return status < 500;
      },
    });

    switch (response.status) {
      case 201:
      case 202:
        leaseResponse = { status: LeaseResponseStatus.LeaseOk, leaseToken: response.data };
        break;
      case 203:
        leaseResponse = { status: LeaseResponseStatus.NoMatchingLicense, data: response.data };
        break;
      case 300:
        leaseResponse = { status: LeaseResponseStatus.MultiLicense, data: response.data };
        break;
      case 400:
        // 400 error means user has no ACO account. Should not happen in prodoction.
        // Can happen in e2e tests, because we use cloud users without ACO acocunt
        leaseResponse = { status: LeaseResponseStatus.NoAccount, data: response.data };
        break;
      case 401:
        leaseResponse = { status: LeaseResponseStatus.Unauthorized, data: response.data };
        break;
      case 403:
        leaseResponse = { status: LeaseResponseStatus.NoMatchingLicense, data: response.data };
        break;
      case 409:
        leaseResponse = { status: LeaseResponseStatus.LicenseConflict, data: response.data };
        break;
      default:
        leaseResponse = { status: LeaseResponseStatus.LeaseError, data: response.data };
        break;
    }
  } catch (error) {
    if (error.response) {
      leaseResponse = { status: LeaseResponseStatus.ServerError, data: error.response.data };
    } else if (error.request) {
      leaseResponse = { status: LeaseResponseStatus.NoAnswer };
    } else {
      // Something happened in setting up the request
      throw error;
    }
  }

  return leaseResponse;
};

const returnLeaseToApi = async (leaseId: string) => {
  const headers = await getRequestHeaders();

  let retVal: { status: number; data: '' } = null;
  try {
    const response = await faxios.delete(`${config.acoHost}/api/lease/${leaseId}`, {
      headers,
    });

    retVal = { status: response.status, data: response.data };
  } catch (error) {
    // Treat lease return as oneshot operation and ignore error
    console.error('lease could not be returned', error);
  }

  return retVal;
};

const liberateLease = async (): Promise<KickoutResponseStatus> => {
  const headers = await getRequestHeaders(true);

  const body = getRequestBody();

  let kickoutResponse = KickoutResponseStatus.NoAnswer;

  try {
    const response = await faxios.post(`${config.acoHost}/api/kickout`, body, {
      headers,
      validateStatus(status) {
        return status < 500;
      },
    });

    switch (response.status) {
      case 201:
        kickoutResponse = KickoutResponseStatus.Success;
        break;
      case 304:
        kickoutResponse = KickoutResponseStatus.NoLeaseFound;
        break;
      case 406:
        kickoutResponse = KickoutResponseStatus.LoginNeedsRefresh;
        break;
      default:
        kickoutResponse = KickoutResponseStatus.NoLeaseFound;
        break;
    }
  } catch (error) {
    if (error.response) {
      kickoutResponse = KickoutResponseStatus.ServerError;
    } else if (error.request) {
      kickoutResponse = KickoutResponseStatus.NoAnswer;
    } else {
      // Something happened in setting up the request
      throw error;
    }
  }

  return kickoutResponse;
};

const refreshLoginToKickLease = () => {
  const opt = {
    // eslint-disable-next-line camelcase
    max_age: 90,
    appState: {
      kickLease: true,
      targetUrl: window.location.pathname,
    },
  };

  getAuth0Instance().loginWithRedirect(opt);
};

export { getTrialAvailability, getLeaseFromApi, liberateLease, returnLeaseToApi, refreshLoginToKickLease };
